import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useCart from '@/public/hooks/use-cart'
import useInvoicedSupplement from '@/public/hooks/use-invoiced-supplement'
import { handleQuantityChange, handleRemove, setLoading } from '@/public/redux/cartSlice'
import { selectVerified } from '@/public/redux/recaptchaSlice'
import { numberToCurrency } from '@/shared/helpers/String'
import QuantityInput from '@/public/product/QuantityInput'
import CartMembershipToggle from '@/public/product/CartMembershipToggle'
import { accessToken } from '../../shared/helpers/CSRF'

export default function CartItem({
  data: item = null,
  quantity,
  subscription = false,
  pickupOrDelivery = null,
  customerIsMember,
  cartContainsLabOrTest,
  monthlyMembership,
  membershipPlansEnabled = false,
  annualMembership,
  labCompanyName = null,
}) {
  const verified = useSelector(selectVerified)
  // The monthly-vs-annual switcher swaps this line for one of the two Access
  // products, so it only belongs on those two -- and only while those two are
  // still what this site sells.
  //
  // Both conditions are load-bearing, because each covers the other's gap. The
  // sku test alone still offers it on a cart built before rollout that holds
  // ACCESS: the drawer renders without saving, so the eviction has not run, and
  // toggling posts a membership the flag-on endpoint refuses with a 404. The
  // flag test alone offers it for a catalogue plan sitting in a cart when the
  // flag is turned back off -- quoting Access prices beside that plan and
  // letting the shopper swap it for the product the plans replace.
  const accessSkus = [monthlyMembership?.data?.sku, annualMembership?.data?.sku].filter(Boolean)
  const showIntervalToggle = item?.category === 'membership'
    && accessSkus.includes(item?.sku)
    && !membershipPlansEnabled
  const customerChoseLocalPickup = /pickup/i.test(pickupOrDelivery)
  const dispatch = useDispatch()
  const { cart, mutate } = useCart()
  const isInvoicedSupplement = useInvoicedSupplement(item, cart)

  // What this line's plan promises, frozen onto the line when it was added. The
  // membership is the purchase; these are not cart items and carry no price,
  // quantity stepper or remove button of their own.
  //
  // `includes_display` is the plan's cart inclusions -- the entitlements in
  // prose, and the only place a promise the catalogue cannot model (a premium
  // plan's unlimited visits) is stated at all. Deliberately NOT the plan card's
  // marketing bullets. It falls back to labelling `includes`, the entitlement
  // snapshot, for a cart saved before that list was stamped.
  const includedLabels = item?.includes_display?.length
    ? item.includes_display
    : (item?.includes || []).map((included) => (
      Number(included.quantity) > 1 ? `${included.quantity} x ${included.name}` : included.name
    ))

  // The server names the lab company only on a lab line that has one, so its
  // presence is the whole rule.
  const renderLabCompany = () => (!labCompanyName ? null : (
    <div className="lab-company-label badge bg-root-light-blue text-root-blue d-table mt-2 tiny">
      <i className="bi bi-flask me-1" aria-hidden="true" />
      {`Collected at ${labCompanyName}`}
    </div>
  ))

  const renderIncludes = () => (includedLabels.length === 0 ? null : (
    <div className="cart-list-item-includes mt-2" style={{ fontSize: '80%' }}>
      <span className="d-block fw-bold">Includes</span>
      <ul className="mb-0 ps-3 fw-lighter">
        {includedLabels.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </div>
  ))

  // Mirrors the Remove button's rule: a membership a lab or test depends on stays.
  const removable = item.category !== 'membership' || !cartContainsLabOrTest

  const handleRemoveClick = (e) => {
    e.stopPropagation()

    dispatch(setLoading(true))
    handleRemove(accessToken, cart, item, mutate, dispatch, quantity)
  }

  const handleQuantityInputClick = (newQuantity) => {
    dispatch(setLoading(true))
    handleQuantityChange(accessToken, cart, item, mutate, dispatch, newQuantity, quantity)
  }

  const renderPrice = () => {
    if (isInvoicedSupplement) {
      return (
        <>
          <span>$0.00</span>
          <span className="d-block fw-lighter text-success" style={{ fontSize: '75%' }}>
            Invoiced to employer
          </span>
        </>
      )
    }

    const membershipDiscount = Number(item?.membership_discount_amount) || 0
    const unitPrice = membershipDiscount > 0
      ? (Number(item.price) - membershipDiscount)
      : Number(item.price)
    const displayPrice = unitPrice * quantity
    const displayOriginalPrice = Number(item.original_price) * quantity
    const hasDiscount = Boolean(item?.original_price)
      && unitPrice !== Number(item.original_price)
    const hasContractDiscount = Number(item?.contract_discount_amount) > 0
    const hasMembershipDiscount = membershipDiscount > 0

    const discountLabel = () => {
      if (hasContractDiscount && hasMembershipDiscount) {
        return 'Employer discount + member price'
      }
      if (hasContractDiscount) {
        return 'Employer discount'
      }
      if (hasMembershipDiscount) {
        return 'Member price'
      }
      return null
    }

    if (hasDiscount) {
      return (
        <>
          <span>{numberToCurrency(displayPrice)}</span>
          <span
            className="d-block fw-lighter text-decoration-line-through"
            style={{ opacity: 0.4, fontSize: '88%' }}
          >
            {numberToCurrency(displayOriginalPrice)}
          </span>
          {discountLabel() && (
            <span className="d-block fw-lighter text-success" style={{ fontSize: '75%' }}>
              {discountLabel()}
            </span>
          )}
          {subscription && (
            <>
              {customerIsMember === false && (
                <span className="d-block fw-lighter" style={{ opacity: 0.4, fontSize: '65%' }}>
                  -
                  {item.calculated_subscription_savings_percentage}
                  {' '}
                  Subscribe & Save
                </span>
              )}
              <span className="d-block fw-lighter" style={{ opacity: 0.4, fontSize: '65%' }}>
                Renews every
                {' '}
                {item.subscription_frequency_in_days}
                {' '}
                days
              </span>
            </>
          )}
        </>
      )
    }

    return (
      <>
        <span>{numberToCurrency(Number(item.price) * quantity)}</span>
        {subscription && (
          <span className="d-block fw-lighter" style={{ opacity: 0.4, fontSize: '65%' }}>
            Renews every&nbsp;
            {item.subscription_frequency_in_days === 7
              ? 'week'
              : `${item.subscription_frequency_in_days} days`}
          </span>
        )}
      </>
    )
  }

  return !item ? '' : (
    <div className="cart-list-item">

      {
        /*
         * BEGIN DESKTOP
         */
      }
      <div className="cart-list-item-desktop d-none d-md-flex flex-wrap w-100">
        <div className="cart-list-item-image">
          <a href={item.permalink}>
            <img src={item.featured_image_url} alt={item.featured_image_alt_text} />
          </a>
        </div>

        <div className="cart-list-item-details">
          <div className="cart-list-item-details-name h4">
            <a href={item.permalink} className="text-decoration-none d-block">
              { item.name }
            </a>
            { (subscription || item.category === 'membership')
                && <div className="d-inline-block badge bg-secondary mt-2 tiny">Subscription</div>}
            { renderLabCompany() }
          </div>

          <div className="cart-list-item-details-desc">
            <p className="my-0 py-0">{ item.sales_intro }</p>
            <p className="my-0 py-0">{ item.short_description }</p>
          </div>
          { renderIncludes() }
          { item.quantifiable
            && (
            <QuantityInput
              quantity={quantity}
              handleChange={(e) => handleQuantityInputClick(e)}
              onRemove={removable ? handleRemoveClick : undefined}
            />
            ) }
        </div>

        <div className="cart-list-item-end">
          <div className="cart-list-item-price h5">
            <div>
              {renderPrice()}
            </div>

            { customerChoseLocalPickup
                && <div className="d-inline-block badge bg-secondary mt-2 tiny">Local Pickup</div>}
          </div>

          { item.category === 'membership' ? (
            !cartContainsLabOrTest
              && (
                <button
                  className="btn btn-link text-end"
                  type="button"
                  disabled={!verified}
                  onClick={(e) => handleRemoveClick(e)}
                >
                  Remove
                </button>
              )
          ) : (
            <button
              className="btn btn-link text-end"
              type="button"
              disabled={!verified}
              onClick={(e) => handleRemoveClick(e)}
            >
              Remove
            </button>
          )}
        </div>

        { showIntervalToggle && (
          <div className="cart-list-item-membership-info">
            <span className="pe-3">
              <span className="bold pe-1">Monthly</span>
              { numberToCurrency(monthlyMembership.data.price) }
            </span>
            <CartMembershipToggle
              membership={item}
              monthlyMembership={monthlyMembership}
              annualMembership={annualMembership}
            />
            <div className="ps-3">
              <span>
                <span className="bold pe-1">Annual</span>
                { numberToCurrency(annualMembership.data.price) }
              </span>
              { item.sku === monthlyMembership.data.sku && (
                <div className="save-info">
                  <span>Save over </span>
                  $
                  { Math.ceil((monthlyMembership.data.price * 12) - annualMembership.data.price) }
                  <span id="savings" />
                  !
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {
        /*
         * END DESKTOP
         */
      }
      {
        /*
         * BEGIN MOBILE
         */
      }
      <div className="cart-list-item-mobile d-flex d-md-none">
        <div className="cart-list-item-image">
          <div>
            <a href={item.permalink}>
              <img src={item.featured_image_url} alt={item.featured_image_alt_text} />
            </a>
          </div>
          <div className="cart-list-item-details">
            <div className="cart-list-item-details-name h5">
              <a href={item.permalink} className="text-decoration-none d-block">
                { item.name }
              </a>
              { subscription
                && <div className="d-inline-block badge bg-secondary mt-2 tiny">Subscription</div>}
              { renderLabCompany() }
            </div>

            <div className={`cart-list-item-price h5 ${subscription ? 'border-top mt-3 pt-3' : ''}`}>
              <div>
                {renderPrice()}
              </div>

              { customerChoseLocalPickup
                && <div className="d-inline-block badge bg-secondary mt-2 tiny">Local Pickup</div>}
            </div>

            { renderIncludes() }

          </div>
        </div>
        <div className="cart-list-item-end">
          { item.quantifiable
            && (
            <QuantityInput
              quantity={quantity}
              handleChange={(e) => handleQuantityInputClick(e)}
              onRemove={removable ? handleRemoveClick : undefined}
            />
            ) }
          { showIntervalToggle && (
            <div className="cart-list-item-membership-info">
              <span className="d-flex flex-sm-row flex-column align-items-sm-center text-center pe-1 pe-xs-3">
                <span className="fw-bold pe-sm-1 mb-0">Monthly</span>
                <span>{numberToCurrency(monthlyMembership.data.price)}</span>
              </span>
              <CartMembershipToggle
                membership={item}
                monthlyMembership={monthlyMembership}
                annualMembership={annualMembership}
              />
              <div className="d-flex flex-column flex-sm-row ps-1 ps-xs-3">
                <span className="d-flex flex-column flex-sm-row align-items-sm-center text-center mb-2 mb-sm-0">
                  <span className="fw-bold pe-sm-1">Annual</span>
                  <span>{numberToCurrency(annualMembership.data.price)}</span>
                </span>
                { item.sku === monthlyMembership.data.sku && (
                  <div className="save-info d-none d-xs-inline-block text-center text-sm-start">
                    <span>Save over </span>
                    $
                    {Math.floor((monthlyMembership.data.price * 12) - annualMembership.data.price)}
                    !
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        { item.category === 'membership' ? (
          !cartContainsLabOrTest
          && (
            <button
              className="btn btn-link text-end"
              type="button"
              disabled={!verified}
              onClick={(e) => handleRemoveClick(e)}
            >
              Remove
            </button>
          )
        ) : (
          <button
            className="btn btn-link text-end"
            type="button"
            disabled={!verified}
            onClick={(e) => handleRemoveClick(e)}
          >
            Remove
          </button>
        )}
      </div>
      {
        /*
         * END MOBILE
         */
      }

    </div>
  )
}
